/**
 * App Loading Screen
 * 
 * Displays a loading screen while the AppDataProvider is fetching initial data.
 * Styled to match the initial HTML splash screen for a seamless loading experience.
 */

import React from 'react';
import { useAppData } from './AppDataProvider';

const AppLoadingScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { initialLoadComplete, initialLoadError } = useAppData();

  // Shared styles matching the HTML splash screen
  const splashStyles = `
    .app-loading-screen {
      position: fixed;
      z-index: 9999;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      font-family: Helvetica, 'sans-serif';
      box-sizing: border-box;
      padding: 24px;
      padding-top: max(24px, env(safe-area-inset-top));
      padding-right: max(24px, env(safe-area-inset-right));
      padding-bottom: max(24px, env(safe-area-inset-bottom));
      padding-left: max(24px, env(safe-area-inset-left));
      background-color: #151521;
      background-image: url('/media/login-background/login-background-8.jpeg');
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      color: rgba(255, 255, 255, 0.9);
      line-height: 1;
      font-size: 14px;
      font-weight: 400;
    }
    .app-loading-screen::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
    }
    .app-loading-screen > * {
      position: relative;
      z-index: 1;
    }
    .app-loading-screen__logo {
      display: block;
      margin: 0 0 24px 0;
      width: auto;
      height: auto;
      max-width: min(80vw, 420px);
      max-height: min(22vh, 140px);
      object-fit: contain;
    }
    .app-loading-screen__content {
      text-align: center;
    }
    .app-loading-screen__spinner {
      margin-bottom: 16px;
    }
    .app-loading-screen__spinner .spinner-border {
      color: rgba(255, 255, 255, 0.85) !important;
    }
    .app-loading-screen__text {
      color: rgba(255, 255, 255, 0.85);
    }
    .app-loading-screen__text h5 {
      color: rgba(255, 255, 255, 0.9);
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 8px;
    }
    .app-loading-screen__text p {
      color: rgba(255, 255, 255, 0.7);
      font-size: 14px;
      margin: 0;
    }
    .app-loading-screen__error-icon {
      font-size: 3rem;
      color: #ffc107;
      margin-bottom: 16px;
    }
    .app-loading-screen .btn-primary {
      background-color: #3699ff;
      border-color: #3699ff;
    }
    .app-loading-screen .btn-primary:hover {
      background-color: #187de4;
      border-color: #187de4;
    }
  `;

  // Show loading screen while initial data is being fetched
  if (!initialLoadComplete) {
    return (
      <div className="app-loading-screen">
        <img 
          src="/media/logos/app-icon-source.png" 
          alt="Web Construct Logo" 
          className="app-loading-screen__logo"
        />
        <div className="app-loading-screen__content">
          <div className="app-loading-screen__spinner">
            <div className="spinner-border" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <div className="app-loading-screen__text">
            <h5>Loading Web Construct</h5>
            <p>Fetching your data...</p>
          </div>
        </div>
        <style>{splashStyles}</style>
      </div>
    );
  }

  // Show error state if initial load failed
  if (initialLoadError) {
    return (
      <div className="app-loading-screen">
        <img 
          src="/media/logos/app-icon-source.png" 
          alt="Web Construct Logo" 
          className="app-loading-screen__logo"
        />
        <div className="app-loading-screen__content">
          <div className="app-loading-screen__error-icon">
            <i className="bi bi-exclamation-triangle"></i>
          </div>
          <div className="app-loading-screen__text">
            <h5>Some data failed to load</h5>
            <p style={{ marginBottom: '16px' }}>{initialLoadError}</p>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Reload App
            </button>
          </div>
        </div>
        <style>{splashStyles}</style>
      </div>
    );
  }

  // Render children once data is loaded
  return <>{children}</>;
};

export default AppLoadingScreen;
